import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './VaultInfo.css';

function VaultInfo({ user, token }) {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  const features = [
    {
      icon: '🔐',
      title: 'Secure Storage',
      description: 'Your ARCO tokens are protected with industry-leading encryption and security protocols. Your keys, your crypto.'
    },
    {
      icon: '⚡',
      title: 'Instant Transactions',
      description: 'Send and receive ARCO tokens instantly on the Polygon network with minimal gas fees.'
    },
    {
      icon: '📊',
      title: 'Portfolio Tracking',
      description: 'Monitor your ARCO holdings in real-time with detailed balance history and transaction records.'
    },
    {
      icon: '🔗',
      title: 'Polygon Network',
      description: 'Built on Polygon for fast, affordable transactions while maintaining Ethereum-level security.'
    },
    {
      icon: '🛡️',
      title: 'Non-Custodial',
      description: 'You maintain full control of your private keys. We never have access to your funds.'
    },
    {
      icon: '🌐',
      title: 'Web3 Integration',
      description: 'Seamlessly connect with the Arcolia ecosystem and other Web3 applications.'
    }
  ];

  const dataSafetyItems = [
    {
      icon: '🔒',
      title: 'Encrypted Data',
      description: 'All sensitive data is encrypted both in transit and at rest.'
    },
    {
      icon: '👤',
      title: 'Privacy First',
      description: 'We collect only essential data required for wallet functionality.'
    },
    {
      icon: '🚫',
      title: 'No Tracking',
      description: 'We do not track your transactions or share data with third parties.'
    },
    {
      icon: '✅',
      title: 'Open Source',
      description: 'Our security practices are transparent and auditable.'
    }
  ];

  return (
    <div className="vault-info-page" data-testid="vault-info-page">
      {/* Background */}
      <div className="vault-background">
        <div className="vault-overlay"></div>
        <div className="vault-particles"></div>
      </div>

      {/* Header */}
      <header className="vault-header">
        <button 
          className="back-to-guild-btn"
          onClick={() => navigate('/guild-hall')}
          data-testid="back-to-guild"
        >
          ← Return to Guild Hall
        </button>
        <div className="vault-logo">
          <span className="vault-icon">🏛️</span>
          <h1>The Arcolian Vault</h1>
        </div>
        <div className="header-spacer"></div>
      </header>

      {/* Main Content */}
      <main className="vault-content">
        {/* Hero Section */}
        <section className="vault-hero">
          <div className="hero-glow"></div>
          <div className="hero-content">
            <h2>Your Gateway to ARCO</h2>
            <p className="hero-subtitle">
              A secure, non-custodial cryptocurrency wallet designed exclusively for the ARCO token ecosystem.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-icon">🔐</span>
                <span className="stat-label">Secure</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-icon">⚡</span>
                <span className="stat-label">Fast</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-icon">🛡️</span>
                <span className="stat-label">Private</span>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <nav className="vault-nav">
          <button 
            className={`nav-tab ${activeSection === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveSection('overview')}
          >
            Overview
          </button>
          <button 
            className={`nav-tab ${activeSection === 'features' ? 'active' : ''}`}
            onClick={() => setActiveSection('features')}
          >
            Features
          </button>
          <button 
            className={`nav-tab ${activeSection === 'safety' ? 'active' : ''}`}
            onClick={() => setActiveSection('safety')}
          >
            Data Safety
          </button>
          <button 
            className={`nav-tab ${activeSection === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveSection('privacy')}
          >
            Privacy Policy
          </button>
        </nav>

        {/* Content Sections */}
        <div className="vault-sections">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <section className="content-section overview-section">
              <div className="section-header">
                <h3>What is the Arcolian Vault?</h3>
              </div>
              <div className="overview-content">
                <div className="overview-text">
                  <p>
                    The Arcolian Vault is a dedicated cryptocurrency wallet crafted for members of the Arcolia community. 
                    Built specifically for managing ARCO tokens on the Polygon network, it provides a seamless and secure 
                    way to store, send, and receive your digital assets.
                  </p>
                  <p>
                    As a non-custodial wallet, you maintain complete ownership and control of your private keys. 
                    Your funds are never held by us — they remain solely in your possession at all times.
                  </p>
                  <p>
                    Whether you're a seasoned crypto enthusiast or new to the world of digital assets, 
                    the Arcolian Vault offers an intuitive interface designed to make managing your ARCO tokens 
                    as simple as possible.
                  </p>
                </div>
                <div className="overview-highlights">
                  <div className="highlight-card">
                    <span className="highlight-icon">🪙</span>
                    <h4>ARCO Token</h4>
                    <p>Purpose-built for the native token of the Arcolia ecosystem</p>
                  </div>
                  <div className="highlight-card">
                    <span className="highlight-icon">⛓️</span>
                    <h4>Polygon Network</h4>
                    <p>Fast transactions with minimal fees on a proven blockchain</p>
                  </div>
                  <div className="highlight-card">
                    <span className="highlight-icon">🏰</span>
                    <h4>Arcolia Native</h4>
                    <p>Integrated seamlessly with the Arcolia guild experience</p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Features Section */}
          {activeSection === 'features' && (
            <section className="content-section features-section">
              <div className="section-header">
                <h3>Vault Features</h3>
              </div>
              <div className="features-grid">
                {features.map((feature, index) => (
                  <div key={feature.title} className="feature-card" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="feature-icon">{feature.icon}</div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Data Safety Section */}
          {activeSection === 'safety' && (
            <section className="content-section safety-section">
              <div className="section-header">
                <h3>Data Safety</h3>
                <p className="section-subtitle">Your security is our priority</p>
              </div>
              <div className="safety-content">
                <div className="safety-intro">
                  <p>
                    We take the protection of your data seriously. The Arcolian Vault is designed with 
                    privacy and security at its core, ensuring your information and assets remain safe.
                  </p>
                </div>
                <div className="safety-grid">
                  {dataSafetyItems.map((item, index) => (
                    <div key={item.title} className="safety-card" style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className="safety-icon">{item.icon}</div>
                      <div className="safety-info">
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="safety-commitment">
                  <h4>Our Commitment</h4>
                  <ul>
                    <li>We never store your private keys on our servers</li>
                    <li>All wallet operations happen locally on your device</li>
                    <li>We use industry-standard encryption for all data transmission</li>
                    <li>Regular security audits ensure ongoing protection</li>
                    <li>You can export your wallet data at any time</li>
                  </ul>
                </div>
              </div>
            </section>
          )}

          {/* Privacy Policy Section */}
          {activeSection === 'privacy' && (
            <section className="content-section privacy-section">
              <div className="section-header">
                <h3>Privacy Policy</h3>
                <p className="section-subtitle">Last updated: 5 June 2026</p>
              </div>
              <div className="privacy-content">
                <div className="privacy-document">
                  <div className="privacy-intro">
                    <p>
                      This Privacy Policy explains how Arcolian Vault (the "App") collects, uses, stores, discloses, and 
                      protects information when users access or use the App, the related Arcolia website, and 
                      connected ecosystem features.
                    </p>
                    <p>
                      Arcolian Vault is part of the Arcolia ecosystem, a stewarded digital commons. ARCO is designed 
                      as an access token for Arcolia and not as an investment product, promise of profit, or claim of ownership.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>1. Controller and Contact Details</h4>
                    <p><strong>App name:</strong> Arcolian Vault</p>
                    <p><strong>Ecosystem / Developer:</strong> Matt Reid</p>
                    <p><strong>Contact email:</strong> matt@arcolia.space</p>
                    <p><strong>Website:</strong> arcolia.space</p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>2. Scope of This Policy</h4>
                    <p>This Privacy Policy applies to information collected through:</p>
                    <ul>
                      <li>The Arcolian Vault mobile application</li>
                      <li>The Arcolia website and linked web experiences</li>
                      <li>In-app support, onboarding, and account-related communications</li>
                      <li>Wallet connection, access-control, and ecosystem participation features</li>
                    </ul>
                    <p>This Privacy Policy does not apply to third-party websites, wallets, block explorers, app stores, payment providers, or decentralized protocols that operate under their own policies.</p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>3. Information Collected</h4>
                    <p><strong>A. Information provided directly by users:</strong></p>
                    <ul>
                      <li>Name, username, display name, or profile alias</li>
                      <li>Email address</li>
                      <li>Support messages and other communications</li>
                      <li>Onboarding responses and content voluntarily submitted</li>
                    </ul>
                    <p><strong>B. Wallet and blockchain-related information:</strong></p>
                    <ul>
                      <li>Public wallet addresses</li>
                      <li>Transaction hashes and publicly visible blockchain activity</li>
                      <li>Token holdings and access-related wallet states</li>
                      <li>Signatures used to verify wallet ownership</li>
                    </ul>
                    <p><strong>C. Device, technical, and usage information:</strong></p>
                    <ul>
                      <li>Device type, operating system, app version, language, time zone</li>
                      <li>IP address or approximate network information</li>
                      <li>Crash reports and performance logs</li>
                      <li>Interaction information such as pages viewed and features used</li>
                    </ul>
                  </div>

                  <div className="privacy-section-item">
                    <h4>4. How Information Is Used</h4>
                    <p>Information may be used to:</p>
                    <ul>
                      <li>Provide, operate, maintain, and secure the App and ecosystem features</li>
                      <li>Create or manage user accounts or profiles</li>
                      <li>Connect and verify wallets and enable access-controlled features</li>
                      <li>Provide onboarding flows and ecosystem experiences</li>
                      <li>Communicate about support, updates, policy changes, and security notices</li>
                      <li>Monitor usage, diagnose technical issues, and prevent abuse or fraud</li>
                      <li>Comply with legal obligations and enforce terms</li>
                    </ul>
                  </div>

                  <div className="privacy-section-item">
                    <h4>5. Legal Bases for Processing</h4>
                    <p>Where applicable laws such as UK GDPR, EU GDPR, or similar laws apply, information may be processed based on:</p>
                    <ul>
                      <li>Performance of a contract or pre-contractual steps</li>
                      <li>Legitimate interests (security, abuse prevention, service improvement)</li>
                      <li>User consent, where required by law</li>
                      <li>Compliance with legal obligations</li>
                      <li>Protection of vital interests</li>
                    </ul>
                  </div>

                  <div className="privacy-section-item">
                    <h4>6. Wallets, Blockchain Transparency, and User Responsibility</h4>
                    <p>
                      Arcolian Vault supports non-custodial wallet interactions. Users are responsible for safeguarding 
                      recovery phrases, private keys, and wallet credentials.
                    </p>
                    <p>
                      Blockchain transactions may be irreversible. Public wallet addresses, token balances, and related 
                      transaction history may be visible to third parties on public ledgers.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>7. Sharing and Disclosure of Information</h4>
                    <p>Information may be shared in the following circumstances:</p>
                    <ul>
                      <li><strong>Service providers:</strong> Vendors that help operate the App (hosting, analytics, support)</li>
                      <li><strong>Blockchain networks:</strong> Information submitted to public blockchain networks</li>
                      <li><strong>Legal reasons:</strong> Compliance with law, fraud investigation, safety protection</li>
                      <li><strong>Business transfers:</strong> Mergers, acquisitions, or asset sales</li>
                      <li><strong>User consent:</strong> At the user's direction or with explicit consent</li>
                    </ul>
                  </div>

                  <div className="privacy-section-item">
                    <h4>8. Data Retention</h4>
                    <p>
                      Information is retained only for as long as reasonably necessary for the purposes described, 
                      including providing services, maintaining security, keeping required records, and resolving disputes.
                    </p>
                    <p>
                      Public blockchain data may remain available indefinitely and cannot ordinarily be deleted by Arcolia.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>9. Security</h4>
                    <p>
                      Reasonable administrative, technical, and organizational safeguards are used to protect information. 
                      However, no method of transmission is completely secure, and absolute security cannot be guaranteed.
                    </p>
                    <p>
                      Users are responsible for maintaining the security of their devices, accounts, recovery phrases, 
                      and wallet credentials.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>10. Children's Privacy</h4>
                    <p>
                      Arcolian Vault is not intended for children under 13, or under any higher age threshold where 
                      required by local law. Personal information is not knowingly collected from children in violation 
                      of applicable law.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>11. User Rights and Choices</h4>
                    <p>Depending on location and applicable law, users may have rights to:</p>
                    <ul>
                      <li>Access personal information</li>
                      <li>Know what categories of information are collected, used, or disclosed</li>
                      <li>Request correction of inaccurate information</li>
                      <li>Request deletion of information (subject to exceptions)</li>
                      <li>Object to or restrict certain processing</li>
                      <li>Withdraw consent where processing is based on consent</li>
                      <li>Request portability of certain data</li>
                      <li>Complain to a supervisory authority or regulator</li>
                    </ul>
                  </div>

                  <div className="privacy-section-item">
                    <h4>12. International Transfers</h4>
                    <p>
                      Information may be processed in countries other than the country in which a user resides. 
                      Where required by law, reasonable safeguards will be used for international transfers.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>13. Changes to This Privacy Policy</h4>
                    <p>
                      This Privacy Policy may be updated from time to time to reflect changes in the App, legal 
                      requirements, or privacy practices. Updated versions will be posted at a stable public URL.
                    </p>
                  </div>

                  <div className="privacy-section-item">
                    <h4>14. Contact and Requests</h4>
                    <p>Questions, requests, or complaints about this Privacy Policy may be directed to:</p>
                    <p><strong>Arcolia / Arcolian Vault</strong></p>
                    <p>Email: matt@arcolia.space</p>
                    <p>Website: arcolia.space</p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Enter Vault CTA */}
        <section className="vault-cta">
          <div className="cta-glow"></div>
          <div className="cta-content">
            <h3>Ready to Access Your Vault?</h3>
            <p>Begin managing your ARCO tokens with confidence</p>
            <a 
              href="https://app.emergent.sh/share?app=polygon-wallet-1"
              target="_blank"
              rel="noopener noreferrer"
              className="enter-vault-btn"
              data-testid="enter-vault-btn"
            >
              <span className="btn-icon">🏛️</span>
              Enter the Vault
              <span className="btn-arrow">→</span>
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="vault-footer">
        <p>The Arcolian Vault is part of the Arcolia ecosystem</p>
      </footer>
    </div>
  );
}

export default VaultInfo;
